// Client layer (TS-2.5.03) - UC-14, decisions.md D-005. Composes the filter bar, the entry list
// and an expandable entry-detail panel over useAuditQuery/useAuditEntryDetail; applies no
// client-side scope narrowing of its own - a PM's "own actions only" scope (D-005) is enforced by
// AuditManager server-side, so this page simply renders whatever page of AuditEntry the query
// returns for the caller's chosen filters.
import { Fragment, useMemo, useState } from "react";
import type { AuditQueryParams } from "../../api/audit";
import { AuditEntryDetailPanel } from "./AuditEntryDetailPanel";
import { AuditFilterBar } from "./AuditFilterBar";
import { useAuditEntryDetail } from "./hooks/useAuditEntryDetail";
import { useAuditQuery } from "./hooks/useAuditQuery";
import { strings } from "../../strings";

const S = strings.audit;

function formatWhen(atUtc: string): string {
  const parsed = new Date(atUtc);
  return Number.isNaN(parsed.getTime()) ? atUtc : parsed.toLocaleString();
}

export function AuditPage() {
  const [filters, setFilters] = useState<Omit<AuditQueryParams, "pageNumber" | "pageSize">>({});
  const [expandedKey, setExpandedKey] = useState<string | null>(null);

  const params = useMemo<AuditQueryParams>(() => ({ ...filters, pageSize: 50 }), [filters]);
  const { page, loading, error } = useAuditQuery(params);
  const { detail, loading: detailLoading, error: detailError } = useAuditEntryDetail(expandedKey);

  return (
    <section className="eg-audit">
      <h1>{S.heading}</h1>

      <AuditFilterBar onApply={setFilters} />

      {loading && !page && (
        <div role="status" aria-live="polite">
          {S.loading}
        </div>
      )}

      {error && (
        <div role="alert">
          <p>{S.errorTitle}</p>
          <p>{error.message}</p>
        </div>
      )}

      {page && page.items.length === 0 && <p>{S.empty}</p>}

      {page && page.items.length > 0 && (
        <table>
          <caption>{S.heading}</caption>
          <thead>
            <tr>
              <th scope="col">{S.columns.atUtc}</th>
              <th scope="col">{S.columns.actor}</th>
              <th scope="col">{S.columns.action}</th>
              <th scope="col">{S.columns.entity}</th>
              <th scope="col" />
            </tr>
          </thead>
          <tbody>
            {page.items.map((entry) => (
              <Fragment key={entry.entryKey}>
                <tr>
                  <td>{formatWhen(entry.atUtc)}</td>
                  <td>{entry.actorDisplayName}</td>
                  <td>{entry.action}</td>
                  <td>
                    {entry.entityType}
                    {entry.entityKey ? ` (${entry.entityKey})` : ""}
                  </td>
                  <td>
                    <button
                      type="button"
                      onClick={() => setExpandedKey(expandedKey === entry.entryKey ? null : entry.entryKey)}
                    >
                      {S.detail.view}
                    </button>
                  </td>
                </tr>
                {expandedKey === entry.entryKey && (
                  <tr>
                    <td colSpan={5}>
                      {detailLoading && !detail && (
                        <div role="status" aria-live="polite">
                          {S.detail.loading}
                        </div>
                      )}
                      {detailError && (
                        <div role="alert">
                          <p>{S.detail.errorTitle}</p>
                          <p>{detailError.message}</p>
                          {detailError.kind === "notFound" && <p>{S.detail.notFoundHint}</p>}
                        </div>
                      )}
                      {detail && <AuditEntryDetailPanel detail={detail} onClose={() => setExpandedKey(null)} />}
                    </td>
                  </tr>
                )}
              </Fragment>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}
